import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart3, Users, TrendingUp, TrendingDown, BookOpen, Activity, Flame, ArrowUp, ArrowDown, Minus, DollarSign, CheckCircle, XCircle, RefreshCw } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from "recharts";

const TABS = [
  { id: "engagement", label: "Student Engagement", icon: Activity },
  { id: "growth", label: "Growth & Retention", icon: TrendingUp },
  { id: "content", label: "Content Quality", icon: BookOpen },
  { id: "payments", label: "Payments", icon: DollarSign },
] as const;

const COLORS = ["#8b5cf6", "#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#ec4899", "#6366f1", "#14b8a6"];

const STATUS_LABELS: Record<string, string> = {
  active: "Active",
  trial: "Trial",
  trialing: "Trial",
  past_due: "Past Due",
  paused: "Paused",
  cancelled: "Cancelled",
  canceled: "Cancelled",
  expired: "Expired",
  inactive: "Inactive",
};

function formatStatusLabel(status?: string | null): string {
  if (!status) return "No Subscription";
  return STATUS_LABELS[status] ?? status.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}

export default function AdminAnalytics() {
  const [tab, setTab] = useState<string>("engagement");
  const [days, setDays] = useState("30");

  // KPIs (always loaded)
  const { data: kpis } = useQuery<any>({
    queryKey: ["/api/admin/analytics/kpis"],
    queryFn: async () => {
      const res = await fetch("/api/admin/analytics/kpis", { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  // Retention (loaded with growth tab)
  const { data: retention } = useQuery<any>({
    queryKey: ["/api/admin/analytics/retention"],
    queryFn: async () => {
      const res = await fetch("/api/admin/analytics/retention", { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
    enabled: tab === "growth",
  });

  const { data: engagement } = useQuery<any>({
    queryKey: ["/api/admin/analytics/engagement", days],
    queryFn: async () => {
      const res = await fetch(`/api/admin/analytics/engagement?days=${days}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
    enabled: tab === "engagement",
  });

  const { data: growth } = useQuery<any>({
    queryKey: ["/api/admin/analytics/growth", days],
    queryFn: async () => {
      const res = await fetch(`/api/admin/analytics/growth?days=${days}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
    enabled: tab === "growth",
  });

  const { data: content } = useQuery<any>({
    queryKey: ["/api/admin/analytics/content"],
    queryFn: async () => {
      const res = await fetch("/api/admin/analytics/content", { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
    enabled: tab === "content",
  });

  const { data: payments } = useQuery<any>({
    queryKey: ["/api/admin/payment-analytics", days],
    queryFn: async () => {
      const res = await fetch(`/api/admin/payment-analytics?days=${days}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
    enabled: tab === "payments",
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <BarChart3 className="w-6 h-6" />
          Analytics Dashboard
        </h1>
        <div className="flex gap-2">
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="14">Last 14 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* KPI Summary Cards */}
      {kpis && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { label: "DAU Today", value: kpis.dauToday, prev: null },
            { label: "WAU", value: kpis.wauThisWeek, prev: kpis.wauLastWeek },
            { label: "Questions (7d)", value: kpis.questionsThisWeek, prev: kpis.questionsLastWeek },
            { label: "Accuracy", value: `${kpis.accuracyThisWeek}%`, prev: kpis.accuracyLastWeek, suffix: '%' },
            { label: "Active Subs", value: kpis.activeSubs, prev: kpis.activeSubsPrev },
          ].map((kpi, i) => {
            const numValue = typeof kpi.value === 'string' ? parseFloat(kpi.value) : kpi.value;
            const change = kpi.prev != null ? numValue - kpi.prev : null;
            const changePercent = kpi.prev && kpi.prev > 0 ? Math.round((change! / kpi.prev) * 100) : null;
            return (
              <Card key={i}>
                <CardContent className="p-4">
                  <p className="text-xs text-muted-foreground">{kpi.label}</p>
                  <p className="text-2xl font-bold">{typeof kpi.value === 'number' ? kpi.value.toLocaleString() : kpi.value}</p>
                  {change != null && (
                    <div className={`flex items-center gap-1 text-xs mt-1 ${change > 0 ? 'text-green-600' : change < 0 ? 'text-red-500' : 'text-muted-foreground'}`}>
                      {change > 0 ? <ArrowUp className="w-3 h-3" /> : change < 0 ? <ArrowDown className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
                      {changePercent != null ? `${changePercent > 0 ? '+' : ''}${changePercent}% vs last week` : ''}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Tab switcher */}
      <div className="flex gap-1.5 flex-wrap">
        {TABS.map(t => (
          <Button key={t.id} variant={tab === t.id ? "default" : "outline"} size="sm" onClick={() => setTab(t.id)} className="gap-1.5">
            <t.icon className="w-4 h-4" /> {t.label}
          </Button>
        ))}
      </div>

      {/* Engagement Tab */}
      {tab === "engagement" && engagement && (
        <div className="space-y-6">
          {/* DAU Chart */}
          <Card>
            <CardHeader><CardTitle className="text-lg">Daily Active Users & Questions</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={[...(engagement.dailyActive || [])].reverse()}>
                  <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                  <XAxis dataKey="day" tickFormatter={(v: string) => v?.slice(5, 10)} className="text-xs" />
                  <YAxis className="text-xs" />
                  <Tooltip />
                  <Bar dataKey="users" name="Active Users" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="questions" name="Questions" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Subject breakdown + Hourly usage side by side */}
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader><CardTitle className="text-lg">Subject Activity</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {(engagement.subjectActivity || []).map((s: any, i: number) => (
                    <div key={s.subject} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                        <span className="text-sm font-medium capitalize">{s.subject}</span>
                      </div>
                      <div className="flex items-center gap-3 text-sm text-muted-foreground">
                        <span>{Number(s.users)} students</span>
                        <span>{Number(s.attempts).toLocaleString()} attempts</span>
                        <Badge variant="outline">{Number(s.accuracy)}%</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-lg">Usage by Hour (Jamaica Time)</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={engagement.hourlyUsage || []}>
                    <XAxis dataKey="hour" tickFormatter={(v: number) => { const h = v % 12 || 12; return `${h}${v < 12 ? 'am' : 'pm'}`; }} className="text-xs" />
                    <YAxis className="text-xs" />
                    <Tooltip labelFormatter={(v: number) => { const h = v % 12 || 12; return `${h}:00${v < 12 ? 'am' : 'pm'} - ${(h % 12) + 1}:00${v + 1 < 12 ? 'am' : 'pm'}`; }} />
                    <Bar dataKey="attempts" fill="#10b981" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Top users table */}
          <Card>
            <CardHeader><CardTitle className="text-lg">Most Active Students</CardTitle></CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-muted-foreground">
                      <th className="text-left py-2 px-2">#</th>
                      <th className="text-left py-2 px-2">Student</th>
                      <th className="text-left py-2 px-2">Email</th>
                      <th className="text-center py-2 px-2">Grade</th>
                      <th className="text-right py-2 px-2">Questions</th>
                      <th className="text-right py-2 px-2">XP</th>
                      <th className="text-right py-2 px-2">Active Days</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(engagement.topUsers || []).map((u: any, i: number) => (
                      <tr key={i} className="border-b hover:bg-muted/30">
                        <td className="py-2 px-2 font-bold">{i + 1}</td>
                        <td className="py-2 px-2 font-medium">{u.first_name} {u.last_name}</td>
                        <td className="py-2 px-2 text-muted-foreground">{u.email}</td>
                        <td className="py-2 px-2 text-center">{u.grade}</td>
                        <td className="py-2 px-2 text-right">{Number(u.questions).toLocaleString()}</td>
                        <td className="py-2 px-2 text-right">{Number(u.xp).toLocaleString()}</td>
                        <td className="py-2 px-2 text-right">{u.active_days}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Growth Tab */}
      {tab === "growth" && growth && (
        <div className="space-y-6">
          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {(growth.statusDistribution || []).map((s: any) => (
              <Card key={s.subscription_status}>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold">{Number(s.count)}</p>
                  <p className="text-xs text-muted-foreground">{formatStatusLabel(s.subscription_status)}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Signups chart */}
          <Card>
            <CardHeader><CardTitle className="text-lg">Weekly Signups</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={[...(growth.weeklySignups || [])].reverse()}>
                  <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                  <XAxis dataKey="week" tickFormatter={(v: string) => v?.slice(5, 10)} className="text-xs" />
                  <YAxis className="text-xs" />
                  <Tooltip />
                  <Bar dataKey="signups" name="Total Signups" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="converted" name="Converted" fill="#10b981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Grade distribution */}
            <Card>
              <CardHeader><CardTitle className="text-lg">Active Users by Grade</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={(growth.gradeDistribution || []).map((g: any) => ({ name: `Grade ${g.grade}`, value: Number(g.count) }))} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                      {(growth.gradeDistribution || []).map((_: any, i: number) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Plan distribution */}
            <Card>
              <CardHeader><CardTitle className="text-lg">Plan Distribution</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {(growth.planDistribution || []).map((p: any) => (
                    <div key={p.subscription_plan} className="flex items-center justify-between">
                      <span className="text-sm font-medium capitalize">{p.subscription_plan}</span>
                      <Badge>{Number(p.count)}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Referral performance */}
          {(growth.referralPerformance || []).length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-lg">Referral Partners</CardTitle></CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-muted-foreground">
                        <th className="text-left py-2 px-2">Partner</th>
                        <th className="text-left py-2 px-2">Code</th>
                        <th className="text-right py-2 px-2">Total Referrals</th>
                        <th className="text-right py-2 px-2">Active</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(growth.referralPerformance || []).map((r: any, i: number) => (
                        <tr key={i} className="border-b hover:bg-muted/30">
                          <td className="py-2 px-2 font-medium">{r.first_name} {r.last_name}</td>
                          <td className="py-2 px-2 font-mono text-xs">{r.referral_code}</td>
                          <td className="py-2 px-2 text-right">{Number(r.total_referrals)}</td>
                          <td className="py-2 px-2 text-right font-bold">{Number(r.active_referrals)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Signup Funnel */}
          {retention?.funnel && (
            <Card>
              <CardHeader><CardTitle className="text-lg">90-Day Signup Funnel</CardTitle></CardHeader>
              <CardContent>
                <div className="flex items-end justify-around gap-2 h-48">
                  {[
                    { label: "Signups", value: retention.funnel.totalSignups, color: "bg-blue-500" },
                    { label: "Subscribed", value: retention.funnel.subscribed, color: "bg-purple-500" },
                    { label: "Active", value: retention.funnel.active, color: "bg-green-500" },
                    { label: "Active 7d", value: retention.funnel.activeThisWeek, color: "bg-orange-500" },
                  ].map((step, i) => {
                    const maxVal = retention.funnel.totalSignups || 1;
                    const height = Math.max(10, (step.value / maxVal) * 100);
                    const convRate = i > 0 ? Math.round((step.value / maxVal) * 100) : 100;
                    return (
                      <div key={i} className="flex flex-col items-center flex-1">
                        <p className="text-lg font-bold">{step.value}</p>
                        <p className="text-xs text-muted-foreground mb-1">{convRate}%</p>
                        <div className={`w-full max-w-[60px] ${step.color} rounded-t-lg`} style={{ height: `${height}%` }} />
                        <p className="text-xs text-muted-foreground mt-2 text-center">{step.label}</p>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Retention Cohort Table */}
          {retention?.cohorts && (retention.cohorts as any[]).length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-lg">Retention by Signup Cohort</CardTitle></CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-muted-foreground">
                        <th className="text-left py-2 px-2">Cohort Week</th>
                        <th className="text-right py-2 px-2">Size</th>
                        <th className="text-right py-2 px-2">Week 1</th>
                        <th className="text-right py-2 px-2">Week 2</th>
                        <th className="text-right py-2 px-2">Week 3</th>
                        <th className="text-right py-2 px-2">Week 4</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(retention.cohorts as any[]).map((c: any, i: number) => {
                        const size = Number(c.cohort_size) || 1;
                        const w1 = Math.round((Number(c.week1) / size) * 100);
                        const w2 = Math.round((Number(c.week2) / size) * 100);
                        const w3 = Math.round((Number(c.week3) / size) * 100);
                        const w4 = Math.round((Number(c.week4) / size) * 100);
                        const cellColor = (pct: number) => pct >= 60 ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : pct >= 30 ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400' : pct > 0 ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' : 'text-muted-foreground';
                        return (
                          <tr key={i} className="border-b">
                            <td className="py-2 px-2">{c.cohort_week?.slice(0, 10)}</td>
                            <td className="py-2 px-2 text-right font-bold">{size}</td>
                            <td className={`py-2 px-2 text-right font-medium rounded ${cellColor(w1)}`}>{w1}%</td>
                            <td className={`py-2 px-2 text-right font-medium rounded ${cellColor(w2)}`}>{w2}%</td>
                            <td className={`py-2 px-2 text-right font-medium rounded ${cellColor(w3)}`}>{w3}%</td>
                            <td className={`py-2 px-2 text-right font-medium rounded ${cellColor(w4)}`}>{w4}%</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-muted-foreground mt-3">Percentage of users from each signup week who were active in subsequent weeks. Green = 60%+, Yellow = 30%+, Red = below 30%.</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Content Tab */}
      {tab === "content" && content && (
        <div className="space-y-6">
          {/* Question coverage */}
          <Card>
            <CardHeader><CardTitle className="text-lg">Question Coverage by Grade & Subject</CardTitle></CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-muted-foreground">
                      <th className="text-left py-2 px-2">Grade</th>
                      <th className="text-left py-2 px-2">Subject</th>
                      <th className="text-right py-2 px-2">Questions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(content.questionCoverage || []).map((c: any, i: number) => (
                      <tr key={i} className="border-b hover:bg-muted/30">
                        <td className="py-2 px-2">{c.grade}</td>
                        <td className="py-2 px-2 capitalize">{c.subject}</td>
                        <td className="py-2 px-2 text-right font-bold">{Number(c.question_count).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Topic accuracy (hardest topics) */}
          <Card>
            <CardHeader><CardTitle className="text-lg">Topic Difficulty (Last 30 Days)</CardTitle></CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-muted-foreground">
                      <th className="text-left py-2 px-2">Subject</th>
                      <th className="text-left py-2 px-2">Topic</th>
                      <th className="text-right py-2 px-2">Attempts</th>
                      <th className="text-right py-2 px-2">Students</th>
                      <th className="text-right py-2 px-2">Accuracy</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(content.topicAccuracy || []).map((t: any, i: number) => (
                      <tr key={i} className={`border-b hover:bg-muted/30 ${Number(t.accuracy) < 50 ? 'bg-red-50/50 dark:bg-red-900/10' : ''}`}>
                        <td className="py-2 px-2 capitalize">{t.subject}</td>
                        <td className="py-2 px-2">{t.topic}</td>
                        <td className="py-2 px-2 text-right">{Number(t.attempts).toLocaleString()}</td>
                        <td className="py-2 px-2 text-right">{Number(t.unique_students)}</td>
                        <td className="py-2 px-2 text-right">
                          <Badge variant={Number(t.accuracy) < 50 ? "destructive" : Number(t.accuracy) < 70 ? "outline" : "default"}>
                            {Number(t.accuracy)}%
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* AI vs manual performance */}
          <Card>
            <CardHeader><CardTitle className="text-lg">Question Source Performance (Last 30 Days)</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {(content.generationMethodPerformance || []).map((g: any) => (
                  <div key={g.generation_method} className="text-center p-3 rounded-lg bg-muted/30">
                    <p className="text-xs text-muted-foreground capitalize">{g.generation_method || 'Unknown'}</p>
                    <p className="text-xl font-bold">{Number(g.accuracy)}%</p>
                    <p className="text-xs text-muted-foreground">{Number(g.attempts).toLocaleString()} attempts</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Reported questions */}
          {(content.reportedQuestions || []).length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-lg">Pending Reported Questions</CardTitle></CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-muted-foreground">
                        <th className="text-left py-2 px-2">Question ID</th>
                        <th className="text-left py-2 px-2">Subject</th>
                        <th className="text-left py-2 px-2">Topic</th>
                        <th className="text-left py-2 px-2">Reason</th>
                        <th className="text-right py-2 px-2">Reports</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(content.reportedQuestions || []).map((r: any, i: number) => (
                        <tr key={i} className="border-b hover:bg-muted/30">
                          <td className="py-2 px-2 font-mono text-xs">{r.question_id?.slice(0, 8)}...</td>
                          <td className="py-2 px-2 capitalize">{r.subject}</td>
                          <td className="py-2 px-2">{r.topic}</td>
                          <td className="py-2 px-2"><Badge variant="outline" className="text-xs">{r.reason}</Badge></td>
                          <td className="py-2 px-2 text-right font-bold">{Number(r.report_count)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Payments Tab */}
      {tab === "payments" && payments && (() => {
        const s = payments.summary;
        const daily = payments.dailyBreakdown || [];
        const events = payments.recentEvents || [];

        // Failure reason aggregation
        const failureReasons: Record<string, number> = {};
        events.filter((e: any) => e.eventType === 'payment_failed' && e.failureReason).forEach((e: any) => {
          const reason = e.failureReason || 'unknown';
          failureReasons[reason] = (failureReasons[reason] || 0) + 1;
        });
        const failureData = Object.entries(failureReasons).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);

        return s ? (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {[
                { label: "Succeeded", value: s.totalSucceeded, icon: CheckCircle, color: "text-green-500" },
                { label: "Failed", value: s.totalFailed, icon: XCircle, color: "text-red-500" },
                { label: "Success Rate", value: `${s.successRate}%`, icon: TrendingUp, color: "text-blue-500" },
                { label: "Recovered", value: s.totalRecovered, icon: RefreshCw, color: "text-amber-500" },
                { label: "1st Attempt", value: `${s.firstAttemptSuccessRate}%`, icon: Activity, color: "text-purple-500" },
                { label: "Revenue", value: `$${(s.totalRevenue / 100).toLocaleString()}`, icon: DollarSign, color: "text-green-600" },
              ].map((card, i) => (
                <Card key={i}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                      <card.icon className={`w-3.5 h-3.5 ${card.color}`} />
                      {card.label}
                    </div>
                    <p className="text-xl font-bold">{card.value}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Revenue Trend + Failure Breakdown */}
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader><CardTitle className="text-lg">Revenue Trend</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={[...daily].reverse()}>
                      <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                      <XAxis dataKey="date" tickFormatter={(v: string) => v?.slice(5, 10)} className="text-xs" />
                      <YAxis tickFormatter={(v: number) => `$${(v / 100).toFixed(0)}`} className="text-xs" />
                      <Tooltip formatter={(v: number) => [`$${(v / 100).toFixed(2)}`, 'Revenue']} labelFormatter={(v: string) => v?.slice(0, 10)} />
                      <Line type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle className="text-lg">Success vs Failed</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={[...daily].reverse()}>
                      <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                      <XAxis dataKey="date" tickFormatter={(v: string) => v?.slice(5, 10)} className="text-xs" />
                      <YAxis className="text-xs" />
                      <Tooltip />
                      <Bar dataKey="succeeded" name="Succeeded" fill="#10b981" radius={[2, 2, 0, 0]} />
                      <Bar dataKey="failed" name="Failed" fill="#ef4444" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>

            {/* Failure Reasons */}
            {failureData.length > 0 && (
              <Card>
                <CardHeader><CardTitle className="text-lg">Failure Reasons</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {failureData.map((r, i) => (
                      <div key={i} className="flex items-center justify-between">
                        <span className="text-sm capitalize">{r.name.replace(/_/g, ' ')}</span>
                        <Badge variant="destructive">{r.value}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Recent Events */}
            <Card>
              <CardHeader><CardTitle className="text-lg">Recent Payment Events</CardTitle></CardHeader>
              <CardContent>
                {events.length === 0 ? (
                  <p className="text-muted-foreground text-sm py-4 text-center">No payment events recorded yet.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-muted-foreground">
                          <th className="text-left py-2 px-2">Time</th>
                          <th className="text-left py-2 px-2">Student</th>
                          <th className="text-left py-2 px-2">Status</th>
                          <th className="text-right py-2 px-2">Amount</th>
                          <th className="text-center py-2 px-2">Attempt</th>
                          <th className="text-left py-2 px-2">Plan</th>
                          <th className="text-left py-2 px-2">Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {events.slice(0, 30).map((event: any) => (
                          <tr key={event.id} className="border-b hover:bg-muted/30">
                            <td className="py-2 px-2 whitespace-nowrap text-muted-foreground text-xs">
                              {new Date(event.createdAt).toLocaleDateString("en-US", { timeZone: "America/Jamaica", month: "short", day: "numeric" })}{" "}
                              {new Date(event.createdAt).toLocaleTimeString("en-US", { timeZone: "America/Jamaica", hour: "numeric", minute: "2-digit", hour12: true })}
                            </td>
                            <td className="py-2 px-2">
                              <div className="font-medium text-xs">{event.userName}</div>
                              <div className="text-[10px] text-muted-foreground">{event.email}</div>
                            </td>
                            <td className="py-2 px-2">
                              <Badge variant={event.eventType === "payment_succeeded" ? "default" : "destructive"} className="text-[10px]">
                                {event.eventType === "payment_succeeded" ? (event.recovered ? "Recovered" : "Success") : "Failed"}
                              </Badge>
                            </td>
                            <td className="text-right py-2 px-2 font-medium">${(event.amount / 100).toFixed(2)}</td>
                            <td className="text-center py-2 px-2">{event.attemptCount}</td>
                            <td className="py-2 px-2 capitalize text-xs">{event.subscriptionPlan || "-"}</td>
                            <td className="py-2 px-2 text-xs text-muted-foreground">{event.failureReason || (event.recovered ? `After ${event.attemptCount} attempts` : "")}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        ) : <p className="text-muted-foreground text-center py-12">No data available</p>;
      })()}

      {/* Loading state */}
      {((tab === "engagement" && !engagement) || (tab === "growth" && !growth) || (tab === "content" && !content) || (tab === "payments" && !payments)) && (
        <div className="text-center py-12">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto" />
          <p className="mt-4 text-muted-foreground">Loading analytics...</p>
        </div>
      )}
    </div>
  );
}
